import { motion } from "framer-motion";
import { 
  Users, TrendingUp, AlertTriangle, DollarSign, 
  ArrowUpRight, ArrowDownRight, Clock, Target,
  CheckCircle2, XCircle, Minus, BarChart3, PieChart,
  Activity, Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

// Mock dashboard data
const dashboardData = {
  stats: {
    seriousFounders: 12,
    seriousChange: 3,
    conversionRate: 28,
    conversionChange: 5,
    pipelineValue: 145000,
    pipelineChange: 22000,
    avgResponseTime: "18h",
  },
  leads: [
    { id: 1, name: "Sarah Chen", company: "TechFlow", stage: "early-revenue", score: 92, status: "high", submitted: "2 hours ago" },
    { id: 2, name: "Marcus Johnson", company: "DataSync", stage: "validating", score: 78, status: "medium", submitted: "5 hours ago" },
    { id: 3, name: "Emily Rodriguez", company: "HealthKit", stage: "idea", score: 65, status: "nurture", submitted: "1 day ago" },
    { id: 4, name: "Alex Kim", company: "FinanceApp", stage: "growing", score: 88, status: "high", submitted: "1 day ago" },
    { id: 5, name: "Jordan Smith", company: "EduLearn", stage: "validating", score: 45, status: "decline", submitted: "2 days ago" },
    { id: 6, name: "Taylor Brown", company: "RetailOS", stage: "early-revenue", score: 82, status: "high", submitted: "3 days ago" },
  ],
  dropOffReasons: [
    { reason: "Not ready for execution support", count: 8, percentage: 32 },
    { reason: "Budget mismatch", count: 6, percentage: 24 },
    { reason: "Looking for agency services", count: 5, percentage: 20 },
    { reason: "Timeline too rushed", count: 4, percentage: 16 },
    { reason: "No clear problem to solve", count: 2, percentage: 8 },
  ],
  commonProblems: [
    { problem: "Need validation before building", count: 15 },
    { problem: "Struggling to find product-market fit", count: 12 },
    { problem: "Scaling too fast without systems", count: 9 },
    { problem: "Technical execution bottleneck", count: 8 },
    { problem: "Pricing uncertainty", count: 7 },
  ],
  funnel: {
    submitted: 42,
    qualified: 28,
    discovery: 18,
    proposal: 8,
    closed: 4,
  },
  recommendations: [
    "3 high-priority leads waiting for discovery calls",
    "Follow up with 2 nurture leads showing renewed interest",
    "Content opportunity: Address 'pricing uncertainty' in next blog post",
    "Consider capacity: Pipeline exceeds current bandwidth by 20%",
  ],
};

const getStatusBadge = (status) => {
  const variants = {
    high: { label: "High Priority", className: "bg-success/10 text-success border-success/20" },
    medium: { label: "Medium", className: "bg-warm/10 text-warm border-warm/20" },
    nurture: { label: "Nurture", className: "bg-muted text-muted-foreground" },
    decline: { label: "Decline", className: "bg-destructive/10 text-destructive border-destructive/20" },
  };
  const variant = variants[status] || variants.nurture;
  return <Badge variant="outline" className={variant.className}>{variant.label}</Badge>;
};

export default function DashboardPage() {
  // Dashboard is ready to render immediately

  return (
    <div className="flex flex-col bg-muted/20">
      {/* Header */}
      <section className="bg-background border-b border-border/50 py-8">
        <div className="container-wide">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="heading-large text-foreground mb-2">Founder Dashboard</h1>
                <p className="text-muted-foreground">Lead intelligence and pipeline overview</p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Last updated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-8">
        <div className="container-wide">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Serious Founders (Week)</p>
                      <p className="text-3xl font-serif font-medium text-foreground mt-1">{dashboardData.stats.seriousFounders}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <ArrowUpRight className="h-3 w-3 text-success" />
                        <span className="text-xs text-success">+{dashboardData.stats.seriousChange} from last week</span>
                      </div>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                      <Users className="h-6 w-6 text-accent" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Conversion Rate</p>
                      <p className="text-3xl font-serif font-medium text-foreground mt-1">{dashboardData.stats.conversionRate}%</p>
                      <div className="flex items-center gap-1 mt-1">
                        <ArrowUpRight className="h-3 w-3 text-success" />
                        <span className="text-xs text-success">+{dashboardData.stats.conversionChange}% improvement</span>
                      </div>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-success" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Revenue Pipeline</p>
                      <p className="text-3xl font-serif font-medium text-foreground mt-1">${(dashboardData.stats.pipelineValue / 1000).toFixed(0)}k</p>
                      <div className="flex items-center gap-1 mt-1">
                        <ArrowUpRight className="h-3 w-3 text-success" />
                        <span className="text-xs text-success">+${(dashboardData.stats.pipelineChange / 1000).toFixed(0)}k this week</span>
                      </div>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-warm/10 flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-warm" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Response Time</p>
                      <p className="text-3xl font-serif font-medium text-foreground mt-1">{dashboardData.stats.avgResponseTime}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Minus className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Within target</span>
                      </div>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Clock className="h-6 w-6 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-12">
        <div className="container-wide">
          <Tabs defaultValue="leads" className="space-y-6">
            <TabsList className="bg-card border border-border/50">
              <TabsTrigger value="leads" className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <Users className="h-4 w-4 mr-2" />
                Recent Leads
              </TabsTrigger>
              <TabsTrigger value="insights" className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <BarChart3 className="h-4 w-4 mr-2" />
                Insights
              </TabsTrigger>
              <TabsTrigger value="funnel" className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <PieChart className="h-4 w-4 mr-2" />
                Funnel
              </TabsTrigger>
            </TabsList>

            {/* Leads Tab */}
            <TabsContent value="leads">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Recent Submissions</CardTitle>
                  <CardDescription>Founders who submitted their wishlist</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Founder</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.leads.map((lead) => (
                        <TableRow key={lead.id} className="cursor-pointer hover:bg-muted/50">
                          <TableCell className="font-medium">{lead.name}</TableCell>
                          <TableCell>{lead.company}</TableCell>
                          <TableCell>
                            <span className="capitalize text-sm">{lead.stage.replace('-', ' ')}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={lead.score} className="w-16 h-2" />
                              <span className="text-sm text-muted-foreground">{lead.score}</span>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(lead.status)}</TableCell>
                          <TableCell className="text-muted-foreground">{lead.submitted}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Drop-off Reasons */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-warm" />
                      Drop-off Reasons
                    </CardTitle>
                    <CardDescription>Why leads don't convert</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {dashboardData.dropOffReasons.map((item, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-foreground">{item.reason}</span>
                            <span className="text-sm text-muted-foreground">{item.count} ({item.percentage}%)</span>
                          </div>
                          <Progress value={item.percentage} className="h-2" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Common Problems */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                      <Target className="h-5 w-5 text-accent" />
                      Common Founder Problems
                    </CardTitle>
                    <CardDescription>What founders are struggling with</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboardData.commonProblems.map((item, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                          <span className="text-sm text-foreground">{item.problem}</span>
                          <Badge variant="outline">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Recommendations */}
                <Card className="card-elevated lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                      <Activity className="h-5 w-5 text-success" />
                      Recommendations
                    </CardTitle>
                    <CardDescription>Action items based on current data</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {dashboardData.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-foreground">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Funnel Tab */}
            <TabsContent value="funnel">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Conversion Funnel</CardTitle>
                  <CardDescription>Lead journey from submission to close</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(dashboardData.funnel).map(([stage, count], i, arr) => {
                      const percentage = Math.round((count / dashboardData.funnel.submitted) * 100);
                      const prevCount = i > 0 ? arr[i - 1][1] : count;
                      const dropOff = i > 0 ? prevCount - count : 0;
                      
                      return (
                        <div key={stage}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center text-accent font-medium text-sm">
                                {i + 1}
                              </div>
                              <span className="font-medium text-foreground capitalize">{stage}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-lg font-medium text-foreground">{count}</span>
                              <span className="text-muted-foreground ml-2">({percentage}%)</span>
                              {dropOff > 0 && (
                                <span className="text-sm text-destructive ml-3">-{dropOff}</span>
                              )}
                            </div>
                          </div>
                          <div className="relative">
                            <Progress value={percentage} className="h-3" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
